<!--
  StoreDecoratorPicker.vue -- the store-creation decorator picker.

  Card: "Store creation decorator picker" -- https://trello.com/c/7rzA7Dld

  ONE decorator per store, chosen at store level from a list that is
  FETCHED FROM THE BACK OFFICE and capability-filtered (CLAUDE.md routing
  model, refined 2026-07-09).

  ASSIGN AUTOMATICALLY comes FIRST and is the default for a new store
  (Jake, 2026-09-29). It is stored as an explicit value the BACKEND owns
  (``AUTO_DECORATOR``) and hands us as ``automatic_decorator_value`` -- never
  hardcode it here. A legacy null (a store the backend has not hydrated)
  reads as automatic too. The back office then picks a capable decorator per
  batch. It is rendered HERE rather than returned in the roster because it
  is not a decorator, and because it names nobody it needs no roster: it is
  offered and saves even when the directory is stubbed or down. It replaces
  the old "Clear selection" control.

  INTERIM BY DESIGN. The back office will later auto-select decorators
  heuristically and this component disappears. It therefore owns NO
  policy: which decorators qualify, and whether a given one may be
  chosen, are both decided server-side. This is a renderer for a
  server-computed list plus a writer of a single field.

  WHY IT NEVER FILTERS LOCALLY
  ----------------------------
  Every list it shows comes from GET decorator_options already filtered
  on type / is_active / accepts_central_orders AND on covering every
  decoration method in the store's assortment. Re-deriving any of that
  here would create a second opinion that could disagree with the one the
  PATCH enforces -- the user would see an option the save then rejects.

  WHY THE EMPTY STATES ARE SEPARATE
  ---------------------------------
  Three different reasons produce zero NAMED options, and they need three
  different actions from the distributor:

    * stub / failure  -> "we could not ask" (available: false) -- ops
    * nobody qualifies -> "we asked, nobody covers this store" -- call
                          Dustin, or change the assortment
    * genuinely none   -> the back office has no decorators at all

  Collapsing them into one empty dropdown is how a configuration gap gets
  misdiagnosed as a product gap. The `available` flag plus
  `required_method_slugs` are exactly what lets us tell them apart, which
  is why the endpoint returns both.
-->
<template>
  <section class="sdp" data-testid="store-decorator-picker">
    <header class="sdp__head">
      <h3 class="sdp__title">Decorator</h3>
      <p class="sdp__lede">
        Who produces this store's decorated goods. Let the back office
        assign one, or choose one decorator for the whole store; we only
        list shops that can perform every decoration method this store
        uses.
      </p>
    </header>

    <!-- If our own endpoint failed, the saved choice is unknown: showing
         the options would mark "Assign automatically" as selected for a
         store that may have a named decorator. -->
    <p v-if="loading" class="sdp__note" data-testid="decorator-picker-loading">
      Loading decorators...
    </p>
    <p
      v-else-if="loadFailed"
      class="sdp__note sdp__note--muted"
      data-testid="decorator-picker-unavailable"
    >
      <span class="tf-icon sdp__warn-icon">cloud_off</span>
      {{ unavailableMessage }}
    </p>

    <template v-else>
      <!-- Why there may be no NAMED decorator to choose is explained below
           the list; "Assign automatically" is unaffected by all three. -->
      <ul class="sdp__list" data-testid="decorator-picker-list">
        <li v-for="option in options" :key="option.key">
          <button
            type="button"
            class="sdp__option"
            :class="{ 'sdp__option--on': isSelected(option) }"
            role="radio"
            :aria-checked="isSelected(option) ? 'true' : 'false'"
            :data-testid="`decorator-option-${option.key}`"
            :disabled="saving || option.stale"
            @click="choose(option.id)"
          >
            <span class="tf-icon sdp__option-check">
              {{ isSelected(option) ? "radio_button_checked" : "radio_button_unchecked" }}
            </span>
            <span class="sdp__option-main">
              <span
                class="sdp__option-name"
                :data-testid="
                  isSelected(option)
                    ? 'decorator-selected-name'
                    : `decorator-name-${option.key}`
                "
              >{{ option.name }}</span>
              <span
                v-if="option.help"
                class="sdp__option-methods"
                :data-testid="option.helpTestId"
              >
                {{ option.help }}
              </span>
              <span v-if="option.city" class="sdp__option-city">{{ option.city }}</span>
            </span>
          </button>
        </li>
      </ul>

      <!-- This store's saved decorator cannot do a method its products use
           (flag-on-change, Jake 2026-10-28). The SERVER decides and words
           it; this only shows it. A warning, never a block: the store
           still saves. -->
      <p
        v-if="conflict"
        class="sdp__warning"
        role="alert"
        data-testid="decorator-conflict-warning"
      >
        <span class="tf-icon sdp__warn-icon">warning</span>
        {{ conflict.error }}
      </p>

      <!-- We could not ask the back office (stub mode, or it failed). -->
      <p
        v-if="!available"
        class="sdp__note sdp__note--muted"
        data-testid="decorator-picker-unavailable"
      >
        <span class="tf-icon sdp__warn-icon">cloud_off</span>
        {{ unavailableMessage }}
      </p>

      <!-- We asked, and nobody covers this store's decoration methods. -->
      <p
        v-else-if="!decorators.length && requiredMethodSlugs.length"
        class="sdp__note sdp__note--muted"
        data-testid="decorator-picker-none-qualify"
      >
        <span class="tf-icon sdp__warn-icon">search_off</span>
        No decorator can perform every method this store uses
        ({{ requiredMethodLabel }}), so none can be chosen by name. Remove a
        method from the assortment, or ask for a decorator with these
        capabilities to be added.
      </p>

      <!-- The back office genuinely has no decorators on file. -->
      <p
        v-else-if="!decorators.length"
        class="sdp__note sdp__note--muted"
        data-testid="decorator-picker-empty"
      >
        <span class="tf-icon sdp__warn-icon">search_off</span>
        No decorators are available to choose by name yet.
      </p>
    </template>

    <!-- "Saved." appears only after the server confirmed the save, never
         optimistically. -->
    <p v-if="saving" class="sdp__note" data-testid="decorator-picker-saving">
      Saving...
    </p>
    <p
      v-else-if="saved && !error"
      class="sdp__note sdp__note--muted"
      data-testid="decorator-picker-saved"
    >
      Saved.
    </p>

    <p v-if="error" class="sdp__error" data-testid="decorator-picker-error">
      {{ error }}
    </p>
  </section>
</template>

<script>
import api from "@/services/api";

/**
 * Human labels for OUR decoration-method slugs. Display only -- the
 * matching that decides who qualifies happens server-side against the
 * back office's vocabulary. A slug with no entry here falls back to a
 * de-slugified form so a newly-seeded method still reads sensibly
 * instead of vanishing from the explanation.
 */
const METHOD_LABELS = {
  embroidery: "Embroidery",
  screen_print: "Screen Print",
  dtf: "DTF",
  htv: "HTV",
};

function labelForSlug(slug) {
  if (METHOD_LABELS[slug]) return METHOD_LABELS[slug];
  return String(slug || "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default {
  name: "StoreDecoratorPicker",

  props: {
    storeId: { type: [Number, String], default: null },
  },

  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      decorators: [],
      requiredMethodSlugs: [],
      selectedId: null,
      // Server-reported conflict for an already-stranded store, or null.
      conflict: null,
      // The backend's "Assign automatically" value (AUTO_DECORATOR). null
      // only until the first load, or against a backend that predates it.
      automaticValue: null,
      available: false,
      mode: "live",
      fetchError: null,
      loadFailed: false,
      saved: false,
    };
  },

  computed: {
    /**
     * Every option in display order: "Assign automatically" first, then the
     * store's saved decorator if it is NOT offered right now, then the back
     * office's named decorators.
     */
    options() {
      const automatic = {
        key: "automatic",
        id: this.automaticValue,
        automatic: true,
        name: "Assign automatically",
        help:
          "The back office chooses a decorator that can do every " +
          "decoration method this store uses, for each batch of orders.",
        helpTestId: "decorator-automatic-help",
        city: "",
      };
      // A forced decorator the page cannot offer right now (directory
      // stubbed or down, or the shop no longer covers the assortment) is
      // still the store's choice. Without this row nothing would render as
      // selected, and one click on "Assign automatically" would silently
      // re-route the store. Disabled: it can be kept or replaced, not
      // re-chosen, because the PATCH would refuse an id that is not offered.
      const offeredIds = new Set(this.decorators.map((d) => d.id));
      const stale =
        this.selectedId != null &&
        this.selectedId !== this.automaticValue &&
        !offeredIds.has(this.selectedId)
          ? [
              {
                key: "current",
                id: this.selectedId,
                automatic: false,
                stale: true,
                name: `Current: ${this.selectedId} (not currently offered)`,
                help:
                  "This decorator is still the store's choice, but it isn't " +
                  "in the list we can offer right now.",
                helpTestId: "decorator-current-help",
                city: "",
              },
            ]
          : [];
      return [
        automatic,
        ...stale,
        ...this.decorators.map((d) => ({
          key: d.id,
          id: d.id,
          automatic: false,
          name: d.name,
          help: this.methodLabel(d),
          helpTestId: null,
          city: this.cityLine(d),
        })),
      ];
    },

    requiredMethodLabel() {
      return this.requiredMethodSlugs.map(labelForSlug).join(" + ");
    },

    unavailableMessage() {
      if (this.loadFailed) {
        return "We couldn't load this store's decorator setting. Try again shortly.";
      }
      if (this.mode === "stub") {
        // Sandboxes run without DECONETWORK_BASE_URL. Naming the cause
        // keeps this from being read as "there are no decorators".
        return (
          "The decorator directory isn't configured in this environment, " +
          "so a decorator can't be chosen by name here yet."
        );
      }
      return (
        "We couldn't reach the decorator directory just now, so a " +
        "decorator can't be chosen by name. Try again shortly."
      );
    },
  },

  watch: {
    storeId: {
      immediate: true,
      handler(id) {
        if (id != null) this.load();
      },
    },
  },

  methods: {
    isSelected(option) {
      if (option.automatic) {
        // A legacy null has not been hydrated to the explicit value yet,
        // and means the same thing.
        return this.selectedId == null || this.selectedId === this.automaticValue;
      }
      return option.id === this.selectedId;
    },

    methodLabel(d) {
      const keys = Array.isArray(d?.decoration_method_keys)
        ? d.decoration_method_keys
        : [];
      if (!keys.length) return "";
      // THEIR keys come back here (direct_to_film, heat_transfer, ...).
      // De-slugified rather than mapped: this is a description of what
      // the shop told us it does, and inventing our own name for their
      // capability would misrepresent the source.
      return keys.map(labelForSlug).join(", ");
    },

    cityLine(d) {
      const a = d?.address;
      if (!a) return "";
      return [a.city, a.state].filter(Boolean).join(", ");
    },

    async load() {
      if (this.storeId == null) return;
      this.loading = true;
      this.error = null;
      this.loadFailed = false;
      this.saved = false;
      try {
        const { data } = await api.get(
          `/stores/${this.storeId}/decorator_options/`
        );
        this.decorators = Array.isArray(data?.decorators) ? data.decorators : [];
        this.requiredMethodSlugs = Array.isArray(data?.required_method_slugs)
          ? data.required_method_slugs
          : [];
        this.selectedId = data?.selected_decorator_tenant_id || null;
        this.conflict =
          typeof data?.conflict?.error === "string" ? data.conflict : null;
        this.automaticValue = data?.automatic_decorator_value || null;
        this.available = data?.available === true;
        this.mode = data?.mode || "live";
        this.fetchError = data?.error || null;
      } catch (err) {
        // A failed fetch is NOT an empty roster -- rendering it as one
        // would show a clean "no decorators" where the truth is "we did
        // not manage to ask".
        this.available = false;
        this.decorators = [];
        this.loadFailed = true;
        this.fetchError = this.messageFrom(err);
      } finally {
        this.loading = false;
      }
    },

    messageFrom(err) {
      return (
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong."
      );
    },

    async choose(tenantId) {
      if (this.saving || this.storeId == null) return;
      this.saving = true;
      this.error = null;
      this.saved = false;
      const previous = this.selectedId;
      try {
        await api.patch(`/stores/${this.storeId}/decorator/`, {
          decorator_tenant_id: tenantId,
        });
        this.selectedId = tenantId;
        // The server accepted the new choice, so the old pairing's
        // conflict no longer applies.
        this.conflict = null;
        this.saved = true;
      } catch (err) {
        // Roll back to what the server last confirmed. Leaving the
        // optimistic value on screen after a rejected save is how a
        // distributor ends up believing a store is routed when it is not.
        this.selectedId = previous;
        this.error = this.messageFrom(err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.sdp {
  border: 1px solid var(--tf-border-1, #e3e3e6);
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 20px;
  background: var(--tf-bg-1, #fff);
}

.sdp__head {
  margin-bottom: 10px;
}

.sdp__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
}

.sdp__lede {
  font-size: 13px;
  color: var(--tf-fg-3, #5b5b63);
  margin: 0;
}

.sdp__warn-icon {
  font-size: 16px;
}

.sdp__note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  margin: 10px 0 0;
}

.sdp__note--muted {
  color: var(--tf-fg-3, #5b5b63);
}

.sdp__list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sdp__option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: none;
  border: 1px solid var(--tf-border-1, #e3e3e6);
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
}

.sdp__option:disabled {
  cursor: default;
  opacity: 0.6;
}

.sdp__option--on {
  border-color: var(--tf-accent-1, #2f6fed);
  background: var(--tf-accent-bg, #f2f6ff);
}

.sdp__option-check {
  font-size: 18px;
  line-height: 20px;
}

.sdp__option-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sdp__option-name {
  font-size: 14px;
  font-weight: 600;
}

.sdp__option-methods,
.sdp__option-city {
  font-size: 12px;
  color: var(--tf-fg-3, #5b5b63);
}

.sdp__warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  margin: 10px 0 0;
  color: var(--tf-warning-fg, #8a5a00);
}

.sdp__error {
  font-size: 13px;
  color: var(--tf-danger-fg, #b3261e);
  margin: 10px 0 0;
}
</style>
